import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from '@usas/ui-web/wrappers/ReactRouterDom';

import validate from './NewClubRegistrationApprovalValidation';
import { createNewWorkflowObj } from '../utils/WorkflowsUtils';

import UseNewClubRegistrationData from '../../../state/workflow/newClubRegistration/UseNewClubRegistrationData';
import useNewClubRegistrationPaymentData from '../../../state/workflow/newClubRegistration/UseNewClubRegistrationPaymentData';
import useAdminOverridesDataData from '../../../state/workflow/newClubRegistration/UseAdminOverridesData';

import useEmbeddedReportData from '@usas/ui-web/state/embeddedReport/UseEmbeddedReportData';
import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import { navLinks } from '../../../UseNavLinks';

const INITIAL_VIEW_STATE = {
  reportParameters: { clubRegistrationId: '' },
  routeName: ''
};

const useNewClubRegistrationApproval = (gridPagePath) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getEmbeddedReportListForRoute } = useEmbeddedReportData();
  const { putNewClubRegistrationCartItem } = useNewClubRegistrationPaymentData();
  const { newClubRegistrationState, getNewClubRegistrationWorkflow, putNewClubRegistrationWorkflow } = UseNewClubRegistrationData();
  const { adminOverridesState, putNewClubWorkflowOverride } = useAdminOverridesDataData();

  const { formState, errorState, handleSubmit, updateFormState, setIsDirty, onValueTextPairChanged } = useForm(getInitialFormState, submitFormCallback, validate);

  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
  const [clubRegObjState, setClubRegObjState] = useState({});
  const [trySubmitState, setTrySubmitState] = useState(false);

  const addCartItem = () => {
    const clubRegObj = 
    { 
      clubRegistrationId: clubRegObjState.clubRegistrationId, 
      offeringInstanceId: formState.offeringInstanceId === Constants.DEFAULT_ID ? clubRegObjState.offeringInstanceId : formState.offeringInstanceId,
    };

    const putNewClubRegistrationCartItemPromise =
      putNewClubRegistrationCartItem(
        clubRegObjState.completedByPersonId,
        clubRegObjState.orgUnitId,
        clubRegObjState.clubCode,
        formState.feeAmount,
        clubRegObj);

    if (putNewClubRegistrationCartItemPromise ?? false) {
      putNewClubRegistrationCartItemPromise.then((newState) => {
        if (newState ?? false) {
          //do nothing 
        }
      }).catch((e) => {
        //context error
      });
    }
  }

  const onApprovedClicked = () => {
    updateFormState('isRevisit', false);

    if (gridPagePath === navLinks.HQ_NEW_CLUB_REGISTRATION) {
      const wfsteparray = newClubRegistrationState.objData.workflowStep;

      //only allow HQ approval if we already have LSC approval
      const wfstep = wfsteparray.filter(x => x.stepStatus === "Approved" && x.workflowTypeStepId === 2);

      if (wfstep.length > 0) {
        if (formState.lscId !== Constants.DEFAULT_ID) {
          const putNewClubWorkflowOverridePromise = putNewClubWorkflowOverride(clubRegObjState.orgUnitId, formState.lscId, formState.offeringInstanceId);
          if (putNewClubWorkflowOverridePromise !== null) {
            putNewClubWorkflowOverridePromise.then((newState) => {
              if (newState !== null) {
                addCartItem();
              }
            }).catch((e) => {
              //TODO state error
            });
          }
        } else {
          addCartItem();
        }
      }
    }

    setTrySubmitState(true);
  };

  const onRequestClicked = () => {
    updateFormState('isRevisit', true);
    setTrySubmitState(true);
  };

  const onBackClicked = () => {
    navigate(gridPagePath, { state: { isRevisit: location.state?.isRevisit } });
  };

  const onDownloadApplication = () => {
    setViewState({
      ...viewState,
      reportParameters: { clubRegistrationId: clubRegObjState.clubRegistrationId },
      routeName: gridPagePath === navLinks.HQ_NEW_CLUB_REGISTRATION ? 'WORKFLOWS_HQ_NEW_CLUB_REGISTRATION_APPROVALS' : gridPagePath === navLinks.LSC_NEW_CLUB_REGISTRATION ? 'WORKFLOWS_LSC_NEW_CLUB_REGISTRATION_APPROVALS' : '',
    });
  };

  useEffect(() => {
    if (viewState.routeName) {
      const reportListPromise = getEmbeddedReportListForRoute(viewState.routeName);

      if (reportListPromise) {

        reportListPromise.then((stateObj) => {
          if (Array.isArray(stateObj.arrayData)) {
            const listNames = [];
            stateObj.arrayData.forEach(x => {
              if (listNames.filter(a => a.id === x.listName).length === 0) {
                listNames.push({ id: x.listName, name: x.listDisplayName });
              }
            });

            setViewState({
              ...viewState,
              isLoading: false,
              showGrid: false,
              showReport: stateObj.arrayData.length === 1,
              reportInfoId: stateObj.arrayData.length === 1 ? stateObj.arrayData[0].reportInfoId : -1,
              listNames,
              rawReportList: [...stateObj.arrayData],
              reportList: [...stateObj.arrayData]
            });
          } else {
            setViewState({
              ...viewState,
              isLoading: false,
              showGrid: false,
              showReport: false,
              reportInfoId: -1,
              listNames: [],
              rawReportList: [],
              reportList: []
            });
          }
        }).catch((err) => {
          setViewState({
            ...viewState,
            isLoading: false,
            showGrid: false,
            showReport: false,
            reportInfoId: -1,
            listNames: [],
            rawReportList: [],
            reportList: []
          });
        });
      }    
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewState.routeName]);

  useEffect(() => {
    if (location.state?.clubReg?.workflowId > 0) {
      setClubRegObjState(location.state.clubReg);
      getNewClubRegistrationWorkflow(location.state.clubReg.workflowId);
      updateFormState('offeringInstanceId', -1);
      setIsDirty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (trySubmitState === true) {
      handleSubmit();
      setTrySubmitState(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trySubmitState]);

  useEffect(() => {
    if (newClubRegistrationState.isSaved === true) {
      navigate(gridPagePath, { state: { isRevisit: location.state?.isRevisit } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newClubRegistrationState.isSaved]);

  useEffect(() => {
    if (formState.lscId !== clubRegObjState.lscOrgUnitId && formState.lscId !== Constants.DEFAULT_ID) {
      updateFormState('stepNotes', `LSC changed from ${clubRegObjState.lscName} to ${formState.lscName}. `);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.lscId]);

  useEffect(() => {
    if (clubRegObjState.offeringInstanceId) {
      updateFormState('offeringInstanceId', clubRegObjState.offeringInstanceId);
      updateFormState('orgUnitId', clubRegObjState.orgUnitId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubRegObjState]);

  function submitFormCallback() {
    const workflowObjCopy = JSON.parse(JSON.stringify(newClubRegistrationState.objData));
    if (formState.isRevisit !== true) {
      putNewClubRegistrationWorkflow(clubRegObjState.workflowId,
        createNewWorkflowObj(workflowObjCopy, clubRegObjState.workflowStepId, Constants.WORKFLOW_STATUS_APPROVED,
          formState.stepNotes, 0));
    } else {
      putNewClubRegistrationWorkflow(clubRegObjState.workflowId,
        createNewWorkflowObj(workflowObjCopy, clubRegObjState.workflowStepId, Constants.WORKFLOW_STATUS_SEND_BACK,
          formState.stepNotes, 0));
    }
  };

  function getInitialFormState() {
    return {
      stepNotes: '',
      isRevisit: undefined,
      lscId: -1,
      lscName: '',
      offeringInstanceId: Constants.DEFAULT_ID,
      feeAmount: 0,
      orgUnitId: ''
    };
  };

  return {
    location,
    Constants,
    isSaving: newClubRegistrationState.isSaving,
    isLoading: newClubRegistrationState.isObjLoading,
    newClubRegistrationState,
    clubRegObjState,
    formState,
    errorState,
    viewState,
    setViewState,
    INITIAL_VIEW_STATE,
    onFormValueChanged: updateFormState,
    onApprovedClicked,
    onRequestClicked,
    onBackClicked,
    onDownloadApplication,
    onValueTextPairChanged
  };
};

export default useNewClubRegistrationApproval;